import React, { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';
import { apiUrl } from '../UploadDocument/apiUrl';
const SESSION_KEY = 'authSession';

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  ...props
}: HeaderProps): ReactNode {
  const [mobileOpen, setMobileOpen] = useState(true);
  const [isAuth, setIsAuth] = useState(false);
  const [configuration, setConfiguration] = useState({ logo_primary_base64: '' });
  const onClickReturn = () => {
    window.history.back();
  };
  useEffect(() => {
    const checkAuthAndPath = () => {
      const session = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
      if (session && session.expiresAt > Date.now()) {
        setIsAuth(true);
      } else {
        localStorage.removeItem(SESSION_KEY);
        setIsAuth(false);
      }
    };

    // Run immediately
    checkAuthAndPath();

    // Check periodically (every 500ms)
    const intervalId = setInterval(checkAuthAndPath, 500);

    return () => clearInterval(intervalId);
  }, []);
  const handleLogout = () => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('usuario');
    setIsAuth(false);
  };

  useEffect(() => {
    const config = async () => {
      try {
        const response = await fetch(apiUrl + '/api/settings');
        const data = await response.json();

        setConfiguration(data);
      } catch (error) {
        console.error(error);
        return null;
      }
    }
    config();
  }, [])
  return (
    <IconPresentationProvider size="large" IconContainer={ToolButton}>
      {children && (
        <MobileHeaderContent isOpen={mobileOpen} setIsOpen={setMobileOpen}>
          {children}
        </MobileHeaderContent>
      )}

      <NavBar isSticky={isSticky} {...props}>
        <div className="flex h-[48px] w-full items-center justify-between px-4">

          {/* ================= IZQUIERDA ================= */}
          <div className="flex items-center gap-2 overflow-hidden">
            {isReturnEnabled && (
              <div
                className="cursor-pointer"
                onClick={onClickReturn}
                data-cy="return-to-work-list"
              >
                <svg
                  className="h-5 w-5 text-orange-500"
                  fill="#e26522"
                  viewBox="0 0 42 42"
                >
                  <polygon
                    fillRule="evenodd"
                    points="27.066,1 7,21.068 26.568,40.637 31.502,35.704 16.865,21.068 32,5.933"
                  />
                </svg>
              </div>
            )}

            <img
              src={
                configuration.logo_primary_base64
                  ? configuration.logo_primary_base64
                  : '/logo_med.png'
              }
              alt=""
              className="max-sm:hidden max-h-[44px] w-auto object-contain"
            />

            {UndoRedo}

            <div className="border-primary-dark mx-1 h-[25px] border-r" />

            {PatientInfo}

            {Secondary && (
              <>
                <div className="border-primary-dark mx-1 h-[25px] border-r" />
                {Secondary}
              </>
            )}
          </div>

          {/* ================= DERECHA ================= */}
          <div className="flex items-center gap-2">
            {children && (
              <div className="max-sm:hidden flex items-center gap-2">
                {children}
              </div>
            )}

            <img
              src="/logo_med.png"
              alt=""
              className="max-sm:hidden"
              width={170}
            />

            <div className="border-primary-dark mx-1 h-[25px] border-r" />

            {/* ========== DROPDOWN SETTINGS ========== */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-primary hover:bg-primary-dark h-full w-full"
                >
                  <Icons.GearSettings />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end">
                {menuOptions.map((option, index) => {
                  const IconComponent = option.icon
                    ? Icons[option.icon as keyof typeof Icons]
                    : null;

                  return (
                    <DropdownMenuItem
                      key={index}
                      onSelect={option.onClick}
                      className="flex items-center gap-2 py-2"
                    >
                      {IconComponent && (
                        <span className="flex h-4 w-4 items-center justify-center">
                          <Icons.ByName name={IconComponent.name} />
                        </span>
                      )}
                      <span className="flex-1">{option.title}</span>
                    </DropdownMenuItem>
                  );
                })}

                {isAuth && (
                  <DropdownMenuItem
                    className="flex items-center gap-2 py-2"
                    onClick={handleLogout}
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 3.5a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 1 1 0v2A1.5 1.5 0 0 1 9.5 14h-8A1.5 1.5 0 0 1 0 12.5v-9A1.5 1.5 0 0 1 1.5 2h8A1.5 1.5 0 0 1 11 3.5v2a.5.5 0 0 1-1 0z"
                        />
                        <path
                          fillRule="evenodd"
                          d="M4.146 8.354a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H14.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708z"
                        />
                      </svg>
                    </span>
                    <span className="flex-1">Cerrar sesión</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </NavBar>
    </IconPresentationProvider>
  );
}
const MobileHeaderContent = ({
  children,
  isOpen,
  setIsOpen,
}: {
  children: React.ReactNode;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  // --- Constantes de Diseño ---
  const menuClasses = `
    fixed bottom-0 left-0 right-0 w-full bg-plom-main text-white shadow-2xl z-50
    transition-transform duration-300 ease-in-out sm:hidden
    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
    overflow-x-auto h-[70px] scrollbar-none
  `;

  const toggleButtonClasses = `
    fixed bottom-20 p-1 text-white z-50 shadow-lg sm:hidden
    transition-all duration-300 ease-in-out
    ${isOpen ?
      'right-0 bg-red-600 hover:bg-red-700 rounded-l-full' :
      'rounded-r-full left-0 bg-primary-main hover:bg-primary-dark'}
  `;

  // --- Función de Alternancia ---
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* 🔄 Botón de Alternancia (Abre y Cierra) */}
      <button
        onClick={handleToggle}
        className={toggleButtonClasses}
        aria-label={isOpen ? "Cerrar menú inferior" : "Abrir menú inferior"}
      >
        {isOpen ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="h-6 w-6 bi bi-wrench-adjustable transform transition-transform duration-300 scale-x-[-1]"
            viewBox="0 0 16 16"
          >
            <path d="M16 4.5a4.5 4.5 0 0 1-1.703 3.526L13 5l2.959-1.11q.04.3.041.61" />
            <path d="M11.5 9c.653 0 1.273-.139 1.833-.39L12 5.5 11 3l3.826-1.53A4.5 4.5 0 0 0 7.29 6.092l-6.116 5.096a2.583 2.583 0 1 0 3.638 3.638L9.908 8.71A4.5 4.5 0 0 0 11.5 9m-1.292-4.361-.596.893.809-.27a.25.25 0 0 1 .287.377l-.596.893.809-.27.158.475-1.5.5a.25.25 0 0 1-.287-.376l.596-.893-.809.27a.25.25 0 0 1-.287-.377l.596-.893-.809.27-.158-.475 1.5-.5a.25.25 0 0 1 .287.376M3 14a1 1 0 1 1 0-2 1 1 0 0 1 0 2" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="h-6 w-6 bi bi-wrench-adjustable transform transition-transform duration-300"
            viewBox="0 0 16 16"
          >
            <path d="M16 4.5a4.5 4.5 0 0 1-1.703 3.526L13 5l2.959-1.11q.04.3.041.61" />
            <path d="M11.5 9c.653 0 1.273-.139 1.833-.39L12 5.5 11 3l3.826-1.53A4.5 4.5 0 0 0 7.29 6.092l-6.116 5.096a2.583 2.583 0 1 0 3.638 3.638L9.908 8.71A4.5 4.5 0 0 0 11.5 9m-1.292-4.361-.596.893.809-.27a.25.25 0 0 1 .287.377l-.596.893.809-.27.158.475-1.5.5a.25.25 0 0 1-.287-.376l.596-.893-.809.27a.25.25 0 0 1-.287-.377l.596-.893-.809.27-.158-.475 1.5-.5a.25.25 0 0 1 .287.376M3 14a1 1 0 1 1 0-2 1 1 0 0 1 0 2" />
          </svg>
        )}
      </button>

      {/* ⚙️ Contenedor del Menú Inferior */}
      <div className={menuClasses}>
        <div className="flex items-center h-full bg-plom-main min-w-max px-4 gap-2">
          {children} {/* Aquí se renderizan los elementos del menú */}
        </div>
      </div>
    </>
  );
};
export default Header;
